import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from '../../client-portal/utils/apiBase';
import { logger } from '../../utils/logger';

const VendorAuthContext = createContext(null);

let _vendorPortalToken = '';

export const getVendorPortalToken = () =>
  _vendorPortalToken ||
  localStorage.getItem('vendor_portal_token') ||
  localStorage.getItem('client_portal_token') ||
  '';

export const setVendorPortalToken = (token) => {
  _vendorPortalToken = token || '';
  try {
    if (token) {
      localStorage.setItem('vendor_portal_token', token);
      localStorage.setItem('client_portal_token', token);
    } else {
      localStorage.removeItem('vendor_portal_token');
      localStorage.removeItem('client_portal_token');
    }
  } catch {
    /* ignore */
  }
};

export const VendorAuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => getVendorPortalToken());
  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [membership, setMembership] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [activeContext, setActiveContext] = useState(null);
  const [loading, setLoading] = useState(Boolean(getVendorPortalToken()));
  const [error, setError] = useState(null);

  useEffect(() => {
    _vendorPortalToken = accessToken;
    try {
      if (accessToken) {
        localStorage.setItem('vendor_portal_token', accessToken);
        localStorage.setItem('client_portal_token', accessToken);
      } else {
        localStorage.removeItem('vendor_portal_token');
        localStorage.removeItem('client_portal_token');
      }
    } catch {
      /* ignore */
    }
    if (accessToken) {
      fetchVendorIdentity();
      fetchAvailableContexts();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  const fetchAvailableContexts = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/client-portal/contexts`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const json = await res.json();
          setContexts(json.contexts || []);
          setActiveContext(json.active_context || null);
        }
      }
    } catch {
      /* ignore */
    }
  };

  const fetchVendorIdentity = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/api/vendor-portal/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const json = await res.json();
          setUser(json.user);
          setVendor(json.vendor);
          setMembership(json.membership);
          setOrganizations(json.organizations || []);
        } else {
          setError('Invalid API response format.');
        }
      } else {
        const errJson = await res.json().catch(() => ({}));
        setError(errJson.detail || 'Failed to authenticate vendor session.');
        if (res.status === 401) {
          // Only clear token on genuine 401 unauthenticated error
          setAccessToken('');
          setVendorPortalToken('');
        }
        // On 403 Forbidden, keep token intact so user can switch to Vendor context
      }
    } catch (err) {
      logger.error('Failed to fetch vendor identity', err?.message || 'Vendor fetch error');
      setError('Connection error loading vendor portal.');
    } finally {
      setLoading(false);
    }
  };

  const login = (token) => {
    setVendorPortalToken(token);
    setAccessToken(token);
  };

  const logout = () => {
    const currentToken = accessToken || getVendorPortalToken();
    if (currentToken) {
      fetch(`${API_BASE}/api/vendor-portal/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      }).catch(() => {});
    }
    setVendorPortalToken('');
    setAccessToken('');
    setUser(null);
    setVendor(null);
    setMembership(null);
    setOrganizations([]);
    setContexts([]);
    setActiveContext(null);
    setError(null);
  };

  return (
    <VendorAuthContext.Provider
      value={{
        accessToken,
        user,
        vendor,
        membership,
        organizations,
        contexts,
        activeContext,
        loading,
        error,
        login,
        logout,
        refresh: () => {
          fetchVendorIdentity();
          fetchAvailableContexts();
        },
        isAuthenticated: !!accessToken
      }}
    >
      {children}
    </VendorAuthContext.Provider>
  );
};


export const useVendorAuth = () => useContext(VendorAuthContext) || {};
